/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview PaymentModal CashConfirmStep step UI.
 * @module modals/PaymentModal/steps/PaymentModalCashConfirmStep
 */

import Barcode from "react-barcode";
import { ReactComponent as CopyClipboardIcon } from "../../../assets/icons/PaymentModal/CopyClipboardIcon.svg";
import { ReactComponent as CheckmarkIcon } from "../../../assets/icons/PaymentModal/CheckmarkIcon.svg";
import { formatDueDate } from "../../../utils/payment/paymentFormatting.js";

const PaymentModalCashConfirmStep = ({
  styles,
  t,
  cashPaymentReference,
  cashPaymentAmount,
  cashPaymentDueDate,
  isCopied,
  copyToClipboard,
}) => (
          <div className={styles.stepContent}>
            <div className={styles.cashPaymentStepContent}>
              <div className={styles.cashPaymentTitleContainer}>
                <div className={styles.cashPaymentTitle}>
                  {t("components.paymentModal.cashSection2Title")}
                </div>
                <p className={styles.cashPaymentSubtitle}>
                  {t("components.paymentModal.cashSection2Description")}
                </p>
              </div>
              <div className={styles.cashPaymentInformationContent}>
                <div className={styles.cashPaymentTitleContainer}>
                  <div className={styles.cashPaymentTitle}>
                    {t("components.paymentModal.cashSection2Subtitle")}
                  </div>
                  <p className={styles.cashPaymentInstructionsLabel}>
                    {t("components.paymentModal.cashSection2Instructions")}:
                  </p>
                </div>
                <div className={styles.cashPaymentInstructions}>
                  <p className={styles.cashPaymentInstruction}>
                    1. {t("components.paymentModal.cashSection2Instruction1")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    2. {t("components.paymentModal.cashSection2Instruction2")}
                  </p>
                  <p className={styles.cashPaymentInstruction}>
                    3. {t("components.paymentModal.cashSection2Instruction3")}
                  </p>
                </div>

                {/* Barcode Section */}
                <div className={styles.barcodeSection}>
                  <Barcode
                    value={cashPaymentReference}
                    format="CODE128"
                    width={4}
                    height={110}
                  />
                  <div className={styles.barcodeNumberContainer}>
                    <span className={styles.barcodeNumber}>
                      {cashPaymentReference}
                    </span>
                    <button
                      className={`${styles.copyButton} ${
                        isCopied ? styles.copied : ""
                      }`}
                      onClick={() => copyToClipboard(cashPaymentReference)}
                      title={isCopied ? "Copied!" : "Copy to clipboard"}
                    >
                      {isCopied ? <CheckmarkIcon /> : <CopyClipboardIcon />}
                    </button>
                  </div>
                  <span className={styles.cashPaymentBarcodeNumberLabel}>
                    {t("components.paymentModal.cashSection2Label1")}
                  </span>
                </div>

                {/* Cash Payment Details Section */}
                <div className={styles.cashPaymentDetailsSection}>
                  <div className={styles.cashPaymentDetailRow}>
                    <span className={styles.cashPaymentDetailAmount}>
                      ${cashPaymentAmount} MXN
                    </span>
                  </div>
                  <div className={styles.cashPaymentDetailRow}>
                    <span className={styles.cashPaymentDetailLabel}>
                      {t("components.paymentModal.cashSection2Label2")}
                    </span>
                  </div>
                  <div className={styles.cashPaymentDateRow}>
                    <span className={styles.cashPaymentDetailLabel}>
                      {t("components.paymentModal.cashSection2Label3")}
                    </span>
                    <span className={styles.cashPaymentDetailValue}>
                      {formatDueDate(cashPaymentDueDate)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
);

export default PaymentModalCashConfirmStep;
